import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, ExternalLink, CheckCircle, XCircle, Clock, ChevronDown, Github, RefreshCw } from 'lucide-react';

const TestCasesList = () => {
  const [testCases, setTestCases] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [testRunHistory, setTestRunHistory] = useState([]);
  const [issueCounts, setIssueCounts] = useState({});
  const [triageBreakdown, setTriageBreakdown] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedRunId) {
      loadTestRunResults(selectedRunId);
    }
  }, [selectedRunId]);

  const loadData = async () => {
    try {
      // Load test cases
      const casesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test-cases`);
      if (casesResponse.ok) {
        const casesData = await casesResponse.json();
        setTestCases(casesData.test_cases);
      }

      // Load test run history (with cache busting)
      const historyResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/test-cases/results/history?limit=5&_=${Date.now()}`,
        { cache: 'no-store' }
      );
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setTestRunHistory(historyData.test_runs || []);
        
        // Set latest run as default
        if (historyData.test_runs && historyData.test_runs.length > 0) {
          setSelectedRunId(historyData.test_runs[0].run_id);
          setTestResults(historyData.test_runs[0]);
        }
      }

      // Load latest test results (fallback if history fails) with cache busting
      if (!testResults) {
        const resultsResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/test-cases/results/latest?_=${Date.now()}`,
          { cache: 'no-store' }
        );
        if (resultsResponse.ok) {
          const resultsData = await resultsResponse.json();
          setTestResults(resultsData);
        }
      }

      // Load actual triaged issue counts from database (with cache busting)
      const issueCountsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/test-issues/counts-by-test-case?_=${Date.now()}`,
        { cache: 'no-store' }
      );
      if (issueCountsResponse.ok) {
        const countsData = await issueCountsResponse.json();
        setIssueCounts(countsData);
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTestRunResults = async (runId) => {
    // For now, just find from history
    // In future, could add endpoint to get specific run
    const run = testRunHistory.find(r => r.run_id === runId);
    if (run) {
      setTestResults(run);
    }
  };

  const loadTriageBreakdown = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test-issues?_=${Date.now()}`, {
        cache: 'no-store'
      });
      
      if (response.ok) {
        const data = await response.json();
        const issues = data.issues || [];
        
        // Group by test case and status
        const breakdown = {};
        issues.forEach(issue => {
          const testCaseId = issue.test_case_id.toUpperCase();
          if (!breakdown[testCaseId]) {
            breakdown[testCaseId] = {
              fix_now_major: 0,
              fix_now_minor: 0,
              backlog: 0,
              acceptable: 0,
              wontfix: 0
            };
          }
          breakdown[testCaseId][issue.status] = (breakdown[testCaseId][issue.status] || 0) + 1;
        });
        
        setTriageBreakdown(breakdown);
      }
    } catch (error) {
      console.error('Error loading triage breakdown:', error);
    }
  };

  const handleSyncTestCase = async (testCaseId) => {
    setSyncing(prev => ({ ...prev, [testCaseId]: true }));
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/test-issues/sync-test-case/${testCaseId}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const result = await response.json();
        alert(`✅ Synced ${testCaseId.toUpperCase()}:\n- Created: ${result.created}\n- Updated: ${result.updated}\n- Errors: ${result.errors}`);
        
        // Don't reload - keep current test results visible
        // To see GitHub issue links, refresh the comparison page
      } else {
        const error = await response.json();
        alert(`❌ Error syncing: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error syncing test case:', error);
      alert(`❌ Network error: ${error.message}`);
    } finally {
      setSyncing(prev => ({ ...prev, [testCaseId]: false }));
    }
  };

  const handleSyncAll = async () => {
    if (!confirm('Sync ALL triaged issues to GitHub? This will create/update multiple issues.')) {
      return;
    }
    
    setSyncingAll(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/test-issues/sync-all`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const result = await response.json();
        alert(`✅ Synced ALL test cases:\n- Created: ${result.total_created}\n- Updated: ${result.total_updated}\n- Errors: ${result.total_errors}`);
        
        // Don't reload - keep current test results visible until next test run
        // The GitHub sync doesn't change test results, only adds issue URLs to triage data
      } else {
        const error = await response.json();
        alert(`❌ Error syncing: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error syncing all:', error);
      alert(`❌ Network error: ${error.message}`);
    } finally {
      setSyncingAll(false);
    }
  };

  const formatTimestamp = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    if (status === 'pass') return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (status === 'fail') return <XCircle className="w-5 h-5 text-red-600" />;
    return <Clock className="w-5 h-5 text-gray-400" />;
  };

  const getStatusBadge = (status) => {
    if (status === 'pass') {
      return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">Pass</span>;
    }
    if (status === 'fail') {
      return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">Fail</span>;
    }
    return <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">Not Run</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/admin/feedback-dashboard"
              className="flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to admin
            </Link>
            
            <button
              onClick={handleSyncAll}
              disabled={syncingAll}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {syncingAll ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Github className="w-4 h-4" />
                  Sync All to GitHub
                </>
              )}
            </button>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Semantic Test Results</h1>
          <p className="text-gray-600">View test results, triage issues, and sync to GitHub</p>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="mt-4 text-gray-600">Loading test results...</p>
          </div>
        )}

        {!loading && testResults && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            {/* Latest Test Run Info */}
            <div className="mb-6 text-sm text-gray-600">
              Latest test run: {testResults?.run_timestamp ? formatTimestamp(testResults.run_timestamp) : 'N/A'}
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-900">{testResults.summary?.total_tests || 0}</div>
                <div className="text-sm text-blue-700">Total Tests</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-green-900">{testResults.summary?.passed || 0}</div>
                <div className="text-sm text-green-700">Passed</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-red-900">{testResults.summary?.failed || 0}</div>
                <div className="text-sm text-red-700">Failed</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-purple-900">{testResults.summary?.average_score?.toFixed(1) || '0.0'}%</div>
                <div className="text-sm text-purple-700">Average Score</div>
              </div>
            </div>

            {/* Test Cases Results Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Test Case</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Last Run</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Score</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Issues Found</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">% Triaged</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {testCases.map((testCase) => {
                    const result = testResults.test_cases?.[testCase.id.toUpperCase()];
                    const status = result?.status || 'not-run';
                    const score = result?.score || 0;
                    const decisionId = result?.decision_id;
                    const lastRun = result?.last_run_date || testResults?.run_timestamp;
                    const testCaseId = testCase.id.toUpperCase();
                    const breakdown = triageBreakdown[testCaseId] || {};
                    const isSyncing = syncing[testCase.id];
                    
                    // Calculate triage percentage
                    const totalIssues = (result?.critical_issues || 0) + (result?.minor_issues || 0);
                    const triagedCount = Object.values(breakdown).reduce((sum, count) => sum + count, 0);
                    const triagePercent = totalIssues > 0 ? Math.round((triagedCount / totalIssues) * 100) : 0;
                    
                    return (
                      <tr key={testCase.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(status)}
                            <span className="font-medium text-gray-900">{testCase.name}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center text-sm text-gray-600">
                          {lastRun ? formatTimestamp(lastRun) : 'Never'}
                        </td>
                        <td className="py-4 px-4 text-center">
                          {getStatusBadge(status)}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className={`font-bold ${score >= 75 ? 'text-green-600' : 'text-red-600'}`}>
                            {score}%
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center text-sm">
                          {result ? (
                            <div className="space-y-1">
                              <div>
                                <span className="text-red-600 font-medium">{result.critical_issues || 0}</span>
                                <span className="text-gray-600"> critical</span>
                              </div>
                              <div>
                                <span className="text-yellow-600 font-medium">{result.minor_issues || 0}</span>
                                <span className="text-gray-600"> minor</span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-center">
                          {totalIssues > 0 ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className="relative w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="absolute top-0 left-0 h-full bg-green-500 transition-all"
                                  style={{ width: `${triagePercent}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium text-gray-700">{triagePercent}%</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2 justify-center flex-wrap">
                            {decisionId && (
                              <Link
                                to={`/admin/test-cases/${testCase.id}/compare`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 transition-colors font-medium flex items-center gap-1"
                              >
                                Compare
                                <ExternalLink className="w-3 h-3" />
                              </Link>
                            )}
                            
                            {Object.keys(breakdown).length > 0 && (
                              <button
                                onClick={() => handleSyncTestCase(testCase.id)}
                                disabled={isSyncing}
                                className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-1"
                              >
                                {isSyncing ? (
                                  <>
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                    Syncing...
                                  </>
                                ) : (
                                  <>
                                    <Github className="w-3 h-3" />
                                    Sync to GitHub
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !testResults && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Test Results Yet</h2>
            <p className="text-gray-600">Run the test suite to see results here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestCasesList;
